/**
 * Page Error
 *
 * The page-sized failure panel shared by RouteErrorComponent and
 * ErrorBoundary: the same Alert + Retry body every async area uses
 * (AsyncAreaError), a "Go to dashboard" escape hatch, and — in development
 * only — the raw error details.
 *
 * `fullScreen` centres it on a blank screen, for pages with no app frame
 * around them (login, chat, a crash above the layout). Without it
 * the panel sits in the content area, so the admin header and sidebar stay.
 */

import { Home } from 'lucide-react';
import { AsyncAreaError, Button } from '@/components/inputs';
import { cn } from '@/lib/utils';

interface PageErrorProps {
  error: unknown;
  title: string;
  onRetry: () => void;
  onGoHome: () => void;
  /** Raw details shown in development only. */
  details?: string;
  fullScreen?: boolean;
}

export function PageError({ error, title, onRetry, onGoHome, details, fullScreen = false }: PageErrorProps) {
  return (
    <div
      className={cn(
        'flex justify-center',
        fullScreen ? 'min-h-screen items-center bg-background p-4' : 'py-12',
      )}
    >
      <div className="w-full max-w-lg space-y-4">
        <AsyncAreaError error={error} title={title} onRetry={onRetry} />

        {import.meta.env.DEV && details && (
          <details className="bg-muted rounded-lg p-4">
            <summary className="text-sm font-medium cursor-pointer">Error Details</summary>
            <pre className="mt-2 text-xs text-destructive overflow-auto max-h-40 whitespace-pre-wrap">
              {details}
            </pre>
          </details>
        )}

        <Button variant="outline" label="Go to dashboard" preIcon={Home} onClick={onGoHome} />
      </div>
    </div>
  );
}
